import React, { useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { formatApiError } from "@/lib/api";
import { useTheme } from "@/context/ThemeContext";
import { Sun, Moon, HardHat, Lock, ArrowLeft, ShieldCheck, AlertTriangle } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function ResetPassword() {
  const { theme, toggle } = useTheme();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const tokenMissing = useMemo(() => !token.trim(), [token]);
  const tooShort = pw.length > 0 && pw.length < 8;
  const mismatch = confirm.length > 0 && confirm !== pw;

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (pw !== confirm) { toast.error("Passwords don't match"); return; }
    setLoading(true);
    try {
      await axios.post(`${BACKEND}/api/auth/reset-password`, {
        token, new_password: pw,
      });
      toast.success("Password reset. Please sign in.");
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <div className="flex items-center justify-between px-4 md:px-10 py-4 border-b-2 border-foreground dark:border-safety-yellow">
        <Link to="/" className="flex items-center gap-3">
          <span className="inline-flex items-center justify-center h-11 w-11 bg-safety-yellow text-black border-2 border-black dark:border-safety-yellow">
            <HardHat className="w-6 h-6" strokeWidth={3} />
          </span>
          <div className="leading-none">
            <div className="font-heading text-2xl md:text-3xl">KARM<span className="text-safety-yellow">.</span>SAFETY</div>
            <div className="text-[10px] md:text-xs font-bold uppercase tracking-[0.25em] text-muted-foreground mt-1">Certification Tracker v1</div>
          </div>
        </Link>
        <button data-testid="theme-toggle-reset" onClick={toggle}
          className="h-11 w-11 inline-flex items-center justify-center border-2 border-foreground dark:border-safety-yellow hover:bg-safety-yellow hover:text-black transition-colors"
          aria-label="Toggle theme">
          {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </button>
      </div>

      <main className="flex-1 flex items-center justify-center p-4 md:p-10">
        <div className="brutal-card w-full max-w-md p-6 md:p-8 space-y-5">
          <div>
            <div className="inline-block bg-safety-yellow text-black px-2 py-0.5 font-bold uppercase tracking-widest text-[10px] mb-2">Account Recovery</div>
            <h1 className="font-heading text-3xl md:text-4xl">SET NEW PASSWORD</h1>
            <p className="text-sm text-muted-foreground mt-2">
              Choose a strong password you don't use anywhere else.
            </p>
          </div>

          {tokenMissing ? (
            <div data-testid="reset-missing-token" className="border-2 border-status-expired bg-status-expired/10 p-4 space-y-2">
              <div className="inline-flex items-center gap-2 font-heading text-xl">
                <AlertTriangle className="w-5 h-5 text-status-expired" strokeWidth={3} /> MISSING LINK
              </div>
              <p className="text-sm text-muted-foreground">
                This page needs a reset link from your email. Request a new link below.
              </p>
              <Link to="/forgot-password" className="btn-primary w-full">
                Request Reset Link
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider mb-1.5 inline-flex items-center gap-1.5">
                  <Lock className="w-3.5 h-3.5" strokeWidth={3} /> New Password
                </label>
                <input
                  data-testid="reset-password"
                  type="password"
                  required
                  minLength={8}
                  autoComplete="new-password"
                  autoFocus
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  className="industrial-input"
                />
                <div className={`text-[11px] uppercase tracking-wider mt-1 ${tooShort ? "text-status-expired" : "text-muted-foreground"}`}>
                  Minimum 8 characters
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider mb-1.5 inline-flex items-center gap-1.5">
                  <ShieldCheck className="w-3.5 h-3.5" strokeWidth={3} /> Confirm Password
                </label>
                <input
                  data-testid="reset-confirm"
                  type="password"
                  required
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="industrial-input"
                />
                {mismatch && (
                  <div className="text-[11px] uppercase tracking-wider mt-1 text-status-expired">
                    Passwords don't match
                  </div>
                )}
              </div>
              <button
                type="submit"
                data-testid="reset-submit"
                disabled={loading || tooShort || mismatch || !pw || !confirm}
                className="btn-primary w-full disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? "Updating…" : "Update Password"}
              </button>
            </form>
          )}

          <Link
            to="/"
            data-testid="back-to-login-reset"
            className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={3} /> Back to Sign In
          </Link>
        </div>
      </main>

      <footer className="border-t-2 border-foreground dark:border-safety-yellow py-4 px-6 text-center text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
        <div>© {new Date().getFullYear()} KARM LLC. OSHA-aligned certification tracking.</div>
      </footer>
    </div>
  );
}
